/**
 * The public price list.
 *
 * Read from `pricing.snapshot.json`, which is the API's own price list
 * (`GET /api/public/pricing`, the same BillingPlan documents the paywall
 * charges). `scripts/fetch-pricing.mjs` refreshes it on every Cloudflare Pages
 * build, and the admin center triggers a build on every pricing save, so a
 * price changed in the admin reaches this site a few minutes later without a
 * code change. The committed copy is the fallback when the API cannot be
 * reached, and what tests and local dev run against.
 *
 * Razorpay's merchant onboarding checks that the site's prices are the ones
 * checkout honours; reading them from the catalog is what keeps that true.
 *
 * Prices are BEFORE GST (owner decision 2026-10-09): checkout adds GST at
 * GST_RATE_PCT on top, and Razorpay may add its convenience fee. The page has
 * to say so everywhere a price appears, or the checkout total is a surprise.
 */
import snapshot from "./pricing.snapshot.json";

export const GST_RATE_PCT = snapshot.gstRatePct ?? 18;

/** The trial length, as the backend grants it. */
export const TRIAL_DAYS = snapshot.trialDays;

const rupees = (paise) => Math.round(paise / 100);

const plans = [...snapshot.plans].sort(
  (a, b) => (a.display?.sortOrder ?? 0) - (b.display?.sortOrder ?? 0)
);

/** Every term some plan is sold on, shortest first. */
export const TERMS = [...new Set(plans.map((p) => p.termMonths))].sort((a, b) => a - b);

/**
 * Plans grouped into tiers, in catalog order. A tier's words come from its
 * plans' display block; the badge and highlight belong to whichever single
 * plan carries them (the catalog's anchor), lifted to its tier for the cards.
 */
export const TIERS = (() => {
  const byTier = new Map();
  for (const plan of plans) {
    if (!byTier.has(plan.tier)) {
      byTier.set(plan.tier, {
        tier: plan.tier,
        name: plan.display.name,
        tagline: plan.display.tagline || "",
        bullets: plan.display.bullets || [],
        maxMembers: plan.limits?.maxMembers ?? null,
        maxStaff: plan.limits?.maxStaff ?? null,
        prices: {},
        codes: {},
        highlight: false,
        badge: "",
      });
    }
    const tier = byTier.get(plan.tier);
    tier.prices[plan.termMonths] = rupees(plan.pricePaise);
    tier.codes[plan.termMonths] = plan.code;
    if (plan.display.highlight) tier.highlight = true;
    if (plan.display.badge && !tier.badge) tier.badge = plan.display.badge;
  }
  return [...byTier.values()];
})();

/**
 * The term the pricing cards open on. Owner decision (2026-10-11): the
 * 3-month pill, because it shows the lowest sticker price first; the longer
 * terms are one tap away and say what they save. Falls back to the catalog's
 * anchor plan if 3 months is ever not sold. Prices still come from the
 * catalog; only which pill is pressed first is chosen here.
 */
export const PREFERRED_TERM = 3;
export const DEFAULT_TERM = TERMS.includes(PREFERRED_TERM)
  ? PREFERRED_TERM
  : plans.find((p) => p.display.highlight)?.termMonths ?? TERMS[TERMS.length - 1];

/**
 * Every tier gets every feature. Tiers differ by member and staff caps and the
 * monthly Auto reminder credits, not by capability. Site copy rather than catalog data: it describes the product,
 * and the paywall's per-plan bullets are a different, shorter list.
 */
export const INCLUDED = [
  "Members, plans and renewals",
  "Payments, PDF receipts and statements, and a running ledger",
  "Attendance with QR check-in: members scan your poster, or you tap a name",
  "Batches with capacity, a trainer and a check-in filter",
  "Workout plans: exercise library, templates and routine PDFs",
  "Payment reminders: send them yourself, or let them go out automatically",
  "Bulk messaging from your own WhatsApp",
  "Collection and revenue analytics",
];

const inr = (rupees) => `₹${rupees.toLocaleString("en-IN")}`;

export const formatPrice = inr;

/** Rounded, matching the seed's `Math.round(price / termMonths)`. */
export const perMonth = (price, termMonths) => Math.round(price / termMonths);

/**
 * The most any tier saves on a longer term against buying the shortest term
 * again and again, as a whole percent rounded down ("save up to 27%").
 * List prices, sale excluded: a sale ends, a term's discount does not. Zero for
 * the shortest term and wherever the longer term does not actually cost less.
 */
export const termSavingPct = (termMonths) => {
  const shortest = TERMS[0];
  if (termMonths <= shortest) return 0;
  const pcts = TIERS.filter((t) => t.prices[shortest] && t.prices[termMonths]).map((t) => {
    const full = t.prices[shortest] * (termMonths / shortest);
    return ((full - t.prices[termMonths]) / full) * 100;
  });
  return pcts.length ? Math.max(0, Math.floor(Math.max(...pcts))) : 0;
};

/** Price spread over the days in the term (a month is 365/12 days). Matches the ::perday short code. */
export const perDay = (price, termMonths) => Math.round(price / ((365 / 12) * termMonths));

/** Price spread over members and years. Matches the ::permember short code. */
export const perMember = (price, members, termMonths) =>
  Math.round(price / members / (termMonths / 12));

/**
 * Rupees saved against buying the same span in the shortest term's chunks.
 *
 * Absolute rupees rather than a percentage, matching the seed: absolute
 * savings outperform percentages with these buyers.
 */
export const savingsVsShortest = (tier, termMonths) => {
  const shortest = TERMS[0];
  const base = tier.prices[shortest];
  if (!base || !tier.prices[termMonths]) return 0;
  return base * (termMonths / shortest) - tier.prices[termMonths];
};

/** "Up to 100" / "Unlimited", for a cap where null means no cap. */
export const capLabel = (n) => (n === null || n === undefined ? "Unlimited" : `Up to ${n}`);

/* Sales -------------------------------------------------------------- */

/**
 * Sales published by the API: live now or starting soon, each with the final
 * price for every plan it covers, computed by the same function checkout
 * uses. The site only decides which one is live at the moment of reading, so
 * a sale that ends between two builds stops showing on its own end date.
 */
export const SALES = (snapshot.sales || []).map((sale) => ({
  ...sale,
  startsAt: sale.startsAt ? new Date(sale.startsAt) : null,
  endsAt: sale.endsAt ? new Date(sale.endsAt) : null,
}));

export function liveSale(now = new Date()) {
  return (
    SALES.find(
      (s) => (!s.startsAt || s.startsAt <= now) && (!s.endsAt || now < s.endsAt)
    ) || null
  );
}

/** The sale price in rupees for one tier and term, or null if the sale skips it. */
export function salePrice(sale, tier, termMonths) {
  const code = tier.codes[termMonths];
  const paise = sale?.prices?.[code];
  return Number.isInteger(paise) ? rupees(paise) : null;
}

export const formatDate = (date) =>
  date.toLocaleDateString("en-IN", { day: "numeric", month: "long" });
